import React, { useEffect, useRef, useState } from 'react'
import api from '../../services/api'
import { Card } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Badge } from '../ui/badge'
import { useAuth } from '../../context/AuthContext'

function AiChat({ title, subtitle, context, variant = 'full' }) {
  const [conversationId, setConversationId] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [modelInfo, setModelInfo] = useState(null)
  const [defaultProvider, setDefaultProvider] = useState(null)
  const [suggestedPrompts, setSuggestedPrompts] = useState([])
  const messagesEndRef = useRef(null)

  const { user } = useAuth()
  const billingUserId = user?.id ?? null

  const contextKey =
    (context && context.cloudTalkCallId && `cloudtalk_call:${context.cloudTalkCallId}`) ||
    (context && context.passpCustomerId && `passp_customer:${context.passpCustomerId}`) ||
    (context && context.negotiationId && `negotiation:${context.negotiationId}`) ||
    (context && context.dossierId && `dossier:${context.dossierId}`) ||
    'global'
  const storageKey = `ai:conversation:${contextKey}`
  const scope =
    (context && context.cloudTalkCallId && 'cloudtalk_call') ||
    (context && context.passpCustomerId && 'passp_customer') ||
    (context && context.negotiationId && 'negotiation') ||
    (context && context.dossierId && 'dossier') ||
    'global'

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    api
      .get('/ai/meta')
      .then((res) => {
        const provider = res.data?.defaultProvider || null
        setDefaultProvider(provider)
      })
      .catch(() => {
        setDefaultProvider(null)
      })
  }, [])

  useEffect(() => {
    api
      .get('/ai/prompts', { params: { scope, language: 'it' } })
      .then((res) => {
        setSuggestedPrompts(res.data?.prompts || [])
      })
      .catch(() => {
        setSuggestedPrompts([])
      })
  }, [scope])

  useEffect(() => {
    const storedId = localStorage.getItem(storageKey)
    if (storedId) {
      const numericId = Number(storedId)
      if (!Number.isNaN(numericId)) {
        setConversationId(numericId)
        api
          .get(`/ai/conversations/${numericId}?limit=50`)
          .then((res) => {
            if (res.data && res.data.conversation) {
              const conv = res.data.conversation
              setModelInfo({
                id: conv.id,
                model: conv.model || null
              })
            }
            if (res.data && Array.isArray(res.data.messages)) {
              const loadedMessages = res.data.messages.map((m) => ({
                id: m.id,
                role: m.role,
                content: m.content
              }))
              setMessages(loadedMessages)
            }
          })
          .catch(() => {
            localStorage.removeItem(storageKey)
          })
      }
    }
  }, [storageKey])

  const handleResetConversation = () => {
    localStorage.removeItem(storageKey)
    setConversationId(null)
    setMessages([])
    setModelInfo(null)
    setError('')
  }

  const handleSend = async () => {
    if (!input.trim()) return
    const userText = input.trim()
    setInput('')
    setError('')

    const newMessages = [...messages, { id: Date.now(), role: 'user', content: userText }]
    setMessages(newMessages)

    try {
      setLoading(true)

      const body = {
        conversationId,
        message: userText,
        context: context || {},
        userId: billingUserId
      }

      const token = localStorage.getItem('token')
      const baseURL = api.defaults.baseURL || '/api'
      const response = await fetch(`${baseURL}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        const contentType = response.headers.get('Content-Type') || ''
        let message = 'Errore nella chiamata AI'
        const text = await response.text()
        if (contentType.includes('application/json')) {
          try {
            const data = JSON.parse(text)
            if (data && data.message) {
              message = data.message
            }
          } catch (_) {
            if (text) message = text
          }
        } else if (text) {
          message = text
        }
        throw new Error(message)
      }

      const convHeader = response.headers.get('X-Conversation-Id')
      if (convHeader) {
        const numericId = Number(convHeader)
        if (!Number.isNaN(numericId)) {
          setConversationId(numericId)
          localStorage.setItem(storageKey, String(numericId))
        }
      }

      let assistantText = ''
      if (response.body && response.body.getReader) {
        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let done = false
        const assistantMessageId = Date.now() + 1
        setMessages((prev) => [...prev, { id: assistantMessageId, role: 'assistant', content: '' }])

        while (!done) {
          const { value, done: readerDone } = await reader.read()
          done = readerDone
          if (value) {
            const chunk = decoder.decode(value)
            assistantText += chunk
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMessageId ? { ...m, content: (m.content || '') + chunk } : m
              )
            )
          }
        }

        if (!assistantText.trim()) {
          const fallback =
            'Nessuna risposta dal modello AI. Verifica la configurazione o la quota del provider nelle impostazioni AI.'
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessageId ? { ...m, content: fallback } : m
            )
          )
          assistantText = fallback
        }
      } else {
        const text = await response.text()
        assistantText = text
        setMessages((prev) => [...prev, { id: Date.now() + 1, role: 'assistant', content: assistantText }])
      }

      if (!conversationId && convHeader) {
        try {
          const convId = Number(convHeader)
          if (!Number.isNaN(convId)) {
            const detailRes = await api.get(`/ai/conversations/${convId}?limit=50`)
            if (detailRes.data && detailRes.data.conversation) {
              setModelInfo({
                id: convId,
                model: detailRes.data.conversation.model || null
              })
            }
          }
        } catch (e) {}
      }
    } catch (e) {
      const message = e?.message || 'Errore durante la richiesta AI'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const renderModelBadge = () => {
    if (!modelInfo || !modelInfo.model) {
      return (
        <Badge variant="secondary" className="text-xs">
          Modello: default (AI_DEFAULT_MODEL)
        </Badge>
      )
    }
    return (
      <Badge variant="outline" className="text-xs">
        Modello: {modelInfo.model}
      </Badge>
    )
  }

  const renderProviderBadge = () => {
    const model = modelInfo && modelInfo.model
    let providerKey = null
    if (model && model.includes(':')) {
      providerKey = model.split(':')[0]
    } else if (defaultProvider) {
      providerKey = defaultProvider
    }
    if (!providerKey) {
      return null
    }
    let label = providerKey
    if (providerKey === 'openai') label = 'OpenAI'
    if (providerKey === 'anthropic') label = 'Anthropic (Claude)'
    if (providerKey === 'google') label = 'Google (Gemini)'
    return (
      <Badge variant="outline" className="text-[10px]">
        Provider: {label}
      </Badge>
    )
  }

  const handlePromptClick = (text) => {
    setInput(text)
  }

  const cardHeightClass =
    variant === 'embedded'
      ? 'h-[68vh]'
      : 'h-[88vh] md:h-[92vh]'

  return (
    <Card className={`flex flex-col ${cardHeightClass}`}>
      <div className="p-4 border-b flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold leading-none">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-wrap justify-end gap-1">
            {renderModelBadge()}
            {renderProviderBadge()}
          </div>
          <div className="flex items-center gap-2">
            {conversationId && (
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                ID {conversationId}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              className="text-[10px] h-6 px-2"
              onClick={handleResetConversation}
            >
              Nuova chat
            </Button>
          </div>
        </div>
      </div>

      {suggestedPrompts.length > 0 && (
        <div className="px-4 pt-2 flex flex-wrap gap-2 border-b pb-2">
          {suggestedPrompts.map((p) => (
            <Button
              key={p.id}
              variant="outline"
              size="sm"
              className="text-[10px] h-6 px-2"
              onClick={() => handlePromptClick(p.prompt_text)}
            >
              {p.title}
            </Button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-xs text-muted-foreground">
            Inizia una conversazione descrivendo cosa ti serve. Il modello userà il contesto della pagina
            (se disponibile) per rispondere in modo più mirato.
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={
              m.role === 'user'
                ? 'flex justify-end'
                : 'flex justify-start'
            }
          >
            <div
              className={
                m.role === 'user'
                  ? 'max-w-[80%] rounded-lg bg-primary text-primary-foreground px-3 py-2 text-xs whitespace-pre-wrap leading-relaxed'
                  : 'max-w-[80%] rounded-lg bg-muted px-3 py-2 text-xs whitespace-pre-wrap leading-relaxed'
              }
            >
              {m.content}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="border-t px-3 py-2 space-y-2">
        {error && (
          <div className="text-[10px] text-red-500">
            {error}
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            rows={2}
            className="flex-1 text-xs rounded-md border bg-background px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary resize-none"
            placeholder="Scrivi un messaggio per l’assistente AI..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          <Button
            size="sm"
            className="text-xs"
            onClick={handleSend}
            disabled={loading || !input.trim()}
          >
            {loading ? 'Invio...' : 'Invia'}
          </Button>
        </div>
      </div>
    </Card>
  )
}

export default AiChat
